<template>
  <CommandDialog
    :open="open"
    :title="t('chat.newConversation.title')"
    :description="t('chat.newConversation.description')"
    @update:open="emit('update:open', $event)"
  >
    <CommandInput :placeholder="t('chat.newConversation.searchPlaceholder')" data-test-id="new-conversation-search" />
    <CommandList>
      <CommandEmpty>{{ t('chat.newConversation.empty') }}</CommandEmpty>
      <CommandGroup :heading="t('chat.newConversation.heading')">
        <CommandItem
          v-for="contact in contactsRef"
          :key="contact.id"
          :value="`${contact.name} ${contact.groupNames.join(' ')}`"
          data-test-id="new-conversation-contact"
          @select="startConversation(contact)"
        >
          <Avatar class="size-8">
            <AvatarImage v-if="contact.picture" :src="contact.picture" alt="" />
            <AvatarFallback class="text-[0.6875rem] font-semibold uppercase">{{ contact.initials }}</AvatarFallback>
          </Avatar>
          <span class="flex min-w-0 flex-col">
            <span class="truncate text-sm font-medium">{{ contact.name }}</span>
            <span class="truncate text-xs text-muted-foreground">{{ contact.groupNames.join(', ') }}</span>
          </span>
        </CommandItem>
      </CommandGroup>
    </CommandList>
  </CommandDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import useAccount from '@/features/auth/hooks/use-account.hook'
import useConversations from '@/features/chat/hooks/use-conversations.hook'
import {
  getNewConversationContacts,
  type NewConversationContactType,
} from '@/features/chat/utils/new-conversation-contacts'
import useRouter from '@/hooks/use-router.hook'
import { useI18n } from '@/lib/i18n'
import { PROFILE_PICTURE_BUCKET_URL, ROUTES } from '@/utils/config/constants'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const { t } = useI18n()
const router = useRouter()
const { accountRef } = useAccount()
const { conversationsRef } = useConversations()

// personnes des groupes de l'utilisateur, tous programmes confondus
const contactsRef = computed(() =>
  getNewConversationContacts(conversationsRef.value, accountRef.value?.id).map((contact) => ({
    ...contact,
    picture: contact.picture ? PROFILE_PICTURE_BUCKET_URL + contact.picture : undefined,
  })),
)

// conversation privee existante : on l'ouvre ; sinon conversation temporaire creee au premier message
function startConversation(contact: NewConversationContactType) {
  emit('update:open', false)
  const target = contact.existingConversationId
    ? ROUTES.CHAT(contact.existingConversationId)
    : `${ROUTES.CHAT()}?sendTo=${contact.id}&programId=${contact.programId}`
  router?.push(target).catch(() => {})
}
</script>
